import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, Upload, Eye, PlayCircle, Trash2, Loader2, X, List } from 'lucide-react';
import VideoUpload from '../VideoUpload';
import EpisodeManager from './EpisodeManager';

const VideoManagement = ({ 
  muxVideos, 
  onDeleteVideo, 
  onAddTrailer, 
  onUploadComplete,
  editingVideo,
  setEditingVideo,
  deletingVideoId,
  onRefreshData
}) => {
  const navigate = useNavigate();
  const [showVideoUpload, setShowVideoUpload] = useState(false);
  const [managingEpisodes, setManagingEpisodes] = useState(null);

  return (
    <div className="space-y-6">
      {/* Upload Button */}
      <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
              <Video className="h-5 w-5 text-electric" />
              <span>Video Management</span>
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Upload and manage video content using Mux streaming
            </p>
          </div>
          <button
            onClick={() => setShowVideoUpload(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-electric text-background rounded-lg font-medium hover:bg-electric/90 transition-colors"
            data-testid="upload-video-btn"
          >
            <Upload className="h-4 w-4" />
            <span>Upload Video</span>
          </button>
        </div>

        {/* Video Status List */}
        <div className="space-y-4">
          <h4 className="text-foreground font-medium">Recent Uploads</h4>
          {muxVideos.length > 0 ? (
            <div className="grid gap-4">
              {muxVideos.map((video) => (
                <div key={video.content_id} className="flex items-center space-x-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                  <div className="w-28 h-16 bg-white/10 rounded-lg overflow-hidden flex-shrink-0">
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url}
                        alt={video.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-foreground font-medium truncate">{video.title}</h4>
                    <div className="flex items-center flex-wrap gap-2 text-sm mt-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        video.status === 'ready' 
                          ? 'bg-green-500/20 text-green-400'
                          : video.status === 'processing'
                          ? 'bg-blue-500/20 text-blue-400'
                          : video.status === 'uploading'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {video.status === 'ready' && '✓ '}
                        {video.status === 'processing' && <Loader2 className="h-3 w-3 inline animate-spin mr-1" />}
                        {video.status.charAt(0).toUpperCase() + video.status.slice(1)}
                      </span>
                      {video.playback_id && (
                        <span className="text-muted-foreground text-xs">ID: {video.playback_id.slice(0, 8)}...</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    {video.status === 'ready' && (
                      <>
                        <button
                          onClick={() => navigate(`/content/${video.content_id}`)}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                          title="View"
                          data-testid={`view-video-${video.content_id}`}
                        >
                          <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                        </button>
                        {video.content_type === 'tv_show' && (
                          <button
                            onClick={() => setManagingEpisodes({ id: video.content_id, title: video.title })}
                            className="p-2 hover:bg-purple-500/20 rounded-lg transition-colors"
                            title="Manage Episodes"
                            data-testid={`manage-episodes-${video.content_id}`}
                          >
                            <List className="h-4 w-4 text-purple-400" />
                          </button>
                        )}
                        <button
                          onClick={() => onAddTrailer(video.content_id)}
                          className="p-2 hover:bg-electric/20 rounded-lg transition-colors"
                          title="Add/Replace Trailer"
                          data-testid={`add-trailer-${video.content_id}`}
                        >
                          <PlayCircle className="h-4 w-4 text-electric" />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => onDeleteVideo(video.content_id)}
                      disabled={deletingVideoId === video.content_id}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                      data-testid={`delete-video-${video.content_id}`}
                    >
                      {deletingVideoId === video.content_id ? (
                        <Loader2 className="h-4 w-4 text-red-400 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4 text-red-400" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white/5 rounded-xl">
              <Video className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No videos uploaded yet</p>
              <p className="text-sm text-muted-foreground/60 mt-1">
                Click &quot;Upload Video&quot; to add your first video
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mux Info */}
      <div className="bg-gradient-to-r from-electric/10 to-pulse/10 border border-electric/20 rounded-2xl p-6">
        <h4 className="text-foreground font-semibold mb-2">About Mux Integration</h4>
        <p className="text-sm text-foreground/70">
          Videos uploaded through this dashboard are processed by Mux, providing adaptive 
          streaming, automatic thumbnail generation, and analytics. Processing typically 
          takes 1-5 minutes depending on video length.
        </p>
      </div>

      {/* Video Upload Modal */}
      {showVideoUpload && (
        <VideoUpload
          onClose={() => setShowVideoUpload(false)}
          onUploadComplete={(data) => {
            onUploadComplete(data);
            setShowVideoUpload(false);
          }}
        />
      )}

      {/* Trailer Upload Modal */}
      {editingVideo?.trailerUploadUrl && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl w-full max-w-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-foreground">Upload Trailer</h3>
              <button 
                onClick={() => setEditingVideo(null)}
                className="p-2 hover:bg-white/10 rounded-lg"
                data-testid="close-trailer-modal"
              >
                <X className="h-5 w-5 text-foreground/60" />
              </button>
            </div>
            <p className="text-sm text-foreground/60 mb-4">
              This trailer will play automatically in the hero section for featured content.
            </p>
            <div className="bg-white/5 border border-white/20 rounded-xl p-6">
              <iframe 
                src={`data:text/html,<html><body style="margin:0;display:flex;justify-content:center;align-items:center;height:100vh;background:%23111;">
                  <script type="module" src="https://cdn.jsdelivr.net/npm/@mux/mux-uploader@latest"></script>
                  <mux-uploader endpoint="${editingVideo.trailerUploadUrl}" 
                    style="--button-background-color:%23FF6B6B;--progress-bar-fill-color:%23FF6B6B;">
                  </mux-uploader>
                </body></html>`}
                className="w-full h-48 rounded-lg border-0"
                title="Trailer Uploader"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => {
                  setEditingVideo(null);
                  onRefreshData();
                }}
                className="px-6 py-2 rounded-lg bg-electric text-background font-medium hover:bg-electric/90"
                data-testid="trailer-done-btn"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Episode Manager Modal */}
      {managingEpisodes && (
        <EpisodeManager
          contentId={managingEpisodes.id}
          contentTitle={managingEpisodes.title}
          onClose={() => {
            setManagingEpisodes(null);
            onRefreshData?.();
          }}
        />
      )}
    </div>
  );
};

export default VideoManagement;
